import axiosClient from "../../../../../../api-client/AxiosClient";
import axiosClientPayload from "../../../../../../api-client/AxiosClientPayload";
/* global globalThis */

const API_BASE_URL = process.env.REACT_APP_PII_BASE_URL;

/**
 * Fetch dataSourceId from MappingDB
 */
export const fetchDataSourceId = async (configId) => {
  try {
    const response = await axiosClient.get(
      `api/MappingDB/CorpMappingDBByUserCorp?OrganizationConfigId=${configId}`,
    );

    const data = response.data;
    if (Array.isArray(data) && data.length > 0) {
      return data[0].dataSourceId;
    }
    return null;
  } catch (err) {
    console.error("Error fetching dataSourceId:", err);
    return null;
  }
};

/**
 * Fetch databases for a data source
 */
export const fetchDatabasesList = async (dataSourceId, configId, token) => {
  try {
    const response = await axiosClientPayload.post(`${API_BASE_URL}/databases`, {
      token: token,
      datasource_id: dataSourceId,
      org_config_id: configId,
    });

    const data = response.data;

    // Handle different response structures
    let databasesList = [];
    if (Array.isArray(data)) {
      databasesList = data;
    } else if (data.databases && Array.isArray(data.databases)) {
      databasesList = data.databases;
    } else if (data.data && Array.isArray(data.data)) {
      databasesList = data.data;
    }

    return databasesList;
  } catch (err) {
    console.error("Error fetching databases:", err);
    throw new Error(err.message || "Failed to fetch databases");
  }
};

/**
 * Fetch tables for a database
 */
export const fetchTablesList = async (
  dataSourceId,
  database,
  configId,
  token,
) => {
  try {
    const response = await axiosClientPayload.post(`${API_BASE_URL}/tables`, {
      token: token,
      datasource_id: dataSourceId,
      database: database,
      schema_name: "dbo",
      org_config_id: configId,
    });

    const data = response.data;

    // Handle different response structures
    let tablesList = [];
    if (Array.isArray(data)) {
      tablesList = data;
    } else if (data.tables && Array.isArray(data.tables)) {
      tablesList = data.tables;
    } else if (data.data && Array.isArray(data.data)) {
      tablesList = data.data;
    }

    return tablesList;
  } catch (err) {
    console.error("Error fetching tables:", err);
    throw new Error(err.message || "Failed to fetch tables");
  }
};

/**
 * Fetch regions
 */
export const fetchRegionsList = async () => {
  try {
    const response = await axiosClient.get(`${API_BASE_URL}/regions`);

    const data = response.data;

    // Handle different response structures
    let regionsList = [];
    if (Array.isArray(data)) {
      regionsList = data;
    } else if (data.regions && Array.isArray(data.regions)) {
      regionsList = data.regions;
    } else if (data.data && Array.isArray(data.data)) {
      regionsList = data.data;
    }

    return regionsList;
  } catch (err) {
    console.error("Error fetching regions:", err);
    return [];
  }
};

/**
 * Map region names to codes
 */
export const mapRegionsToCodes = (regionsList) => {
  const regionCodeMap = {
    "European Union": "EU",
    "United States": "US",
    "United Kingdom": "UK",
    Canada: "CA",
    Australia: "AU",
    Japan: "JP",
    Brazil: "BR",
    Singapore: "SG",
    India: "IN",
    China: "CN",
  };

  const displayMap = {
    EU: "European Union",
    US: "United States",
    UK: "United Kingdom",
    CA: "Canada",
    AU: "Australia",
    JP: "Japan",
    BR: "Brazil",
    SG: "Singapore",
    IN: "India",
    CN: "China",
  };

  const mappedRegions = regionsList.map((region) => {
    const regionStr =
      typeof region === "string" ? region : region.name || region.region_name;
    return regionCodeMap[regionStr] || regionStr;
  });

  const finalRegions =
    mappedRegions.length > 0
      ? mappedRegions
      : ["EU", "US", "UK", "CA", "AU", "JP", "BR", "SG", "IN", "CN"];

  return { finalRegions, displayMap };
};

/**
 * Fetch protection methods
 */
export const fetchProtectionMethodsList = async () => {
  try {
    const response = await axiosClient.get(
      `${API_BASE_URL}/protection-methods`,
    );
    return response.data?.methods || response.data || [];
  } catch (err) {
    console.error("Error fetching protection methods:", err);
    // Return default methods
    return [
      { id: "mask", name: "Masking", description: "Mask sensitive data" },
      { id: "encrypt", name: "Encryption", description: "Encrypt data" },
      {
        id: "tokenize",
        name: "Tokenization",
        description: "Replace with tokens",
      },
      { id: "redact", name: "Redaction", description: "Remove data" },
    ];
  }
};

/**
 * Fetch script protection methods
 */
export const fetchScriptProtectionMethodsList = async () => {
  try {
    const response = await axiosClient.get(
      `${API_BASE_URL}/protection-methods`,
    );
    const data = response.data;
    return {
      reversible: (data.methods || []).filter(
        (m) =>
          data.reversible_methods?.includes(m.name) &&
          m.name?.toLowerCase() !== "decrypt",
      ),
      irreversible: (data.methods || []).filter((m) =>
        data.irreversible_methods?.includes(m.name),
      ),
    };
  } catch (err) {
    console.error("Error fetching script protection methods:", err);
    return { reversible: [], irreversible: [] };
  }
};

/**
 * Analyze/Scan PII data
 */
export const scanPIIData = async (
  database,
  tables,
  region,
  dataSourceId,
  configId,
  token,
) => {
  try {
    const response = await axiosClientPayload.post(`${API_BASE_URL}/scan`, {
      token: token,
      datasource_id: dataSourceId,
      org_config_id: configId,
      database: database,
      tables: tables,
      schema_name: "dbo",
      region: region,
      sample_limit: 100,
    });

    return response.data;
  } catch (err) {
    console.error("Error analyzing PII:", err);
    throw new Error("Failed to analyze PII data. Please try again.");
  }
};

/**
 * Apply protection to columns
 * @param {Object} protectionConfig - Configuration object for protection
 * @param {Object} protectionConfig.scanResults - Scan results data
 * @param {Array} protectionConfig.selectedColumns - Selected columns to protect
 * @param {string} protectionConfig.protectionMethod - Protection method to apply
 * @param {string} protectionConfig.dataSourceId - Data source ID
 * @param {string} protectionConfig.database - Database name
 * @param {string} protectionConfig.region - Region for protection
 * @param {boolean} protectionConfig.previewMode - Preview mode flag
 * @returns {Promise<Array>} Protection results
 */
export const applyProtectionToColumns = async ({
  scanResults,
  selectedColumns,
  protectionMethod,
  dataSourceId,
  database,
  region,
  previewMode,
}) => {
  const configId = localStorage.getItem("organization_config_id");
  const token = localStorage.getItem("token") || "";

  // Group columns by table
  const tableGroups = selectedColumns.reduce((acc, { table, column }) => {
    if (!acc[table]) acc[table] = [];
    acc[table].push(column);
    return acc;
  }, {});

  // Apply protection for each table
  const protectionPromises = Object.entries(tableGroups).map(
    ([tableName, columns]) => {
      return axiosClientPayload.post(`${API_BASE_URL}/protect`, {
        session_ids: scanResults?.session_id ? [scanResults.session_id] : [],
        token: token,
        datasource_id: dataSourceId,
        org_config_id: configId,
        database: database,
        table: tableName,
        schema_name: "dbo",
        method: protectionMethod,
        columns: columns,
        preview_only: previewMode,
        full_table: false,
        region: region,
      });
    },
  );

  return Promise.all(protectionPromises);
};

/**
 * Download protected data
 */
export const downloadProtectedDataFile = async (sessionId, format = "csv") => {
  try {
    const response = await axiosClientPayload.post(
      `${API_BASE_URL}/download`,
      {
        session_id: sessionId,
        format: format,
      },
      { responseType: "blob" },
    );

    const url = globalThis.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `protected_data_${sessionId}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error("Error downloading protected data:", err);
    throw new Error(
      `Failed to download protected data: ${err.response?.data?.detail || err.message}`,
    );
  }
};

/**
 * Generate protection script
 */
export const generateProtectionScript = async (
  sessionIds,
  dbType,
  protectionType,
  method,
) => {
  try {
    const response = await axiosClientPayload.post(
      `${API_BASE_URL}/generate-protection-script`,
      {
        session_ids: sessionIds,
        db_type: dbType,
        protection_type: protectionType,
        method: method,
      },
      { responseType: "blob" },
    );

    const url = globalThis.URL.createObjectURL(new Blob([response.data]));
    return url;
  } catch (err) {
    console.error("Error generating script:", err);
    throw new Error("Failed to generate script. Please try again.");
  }
};

/**
 * Fetch activity logs
 */
export const fetchActivityLogs = async () => {
  try {
    const response = await axiosClient.get(
      "https://func-owlsightpii-dev-weu-ercydcfpguekd7a5.westeurope-01.azurewebsites.net/v1/pii-logs",
    );
    return response.data?.logs || [];
  } catch (err) {
    console.error("Error fetching logs:", err);
    throw new Error("Failed to fetch logs. Please try again.");
  }
};

/**
 * Generate deanonymization script
 */
export const generateDeanonymizationScript = async (logId) => {
  try {
    const response = await axiosClientPayload.post(
      `${API_BASE_URL}/generate-deanonymization-script`,
      { log_id: logId },
      { responseType: "blob" },
    );

    const url = globalThis.URL.createObjectURL(new Blob([response.data]));
    return url;
  } catch (err) {
    console.error("Error generating deanonymization script:", err);
    throw new Error("Failed to generate decryption script. Please try again.");
  }
};

/**
 * Get button text helper functions
 */
export const getDatabaseButtonText = (
  selectedDatabase,
  loadingDatabases,
  databases,
) => {
  if (selectedDatabase) return selectedDatabase;
  if (loadingDatabases) return "Loading databases...";
  if (databases.length === 0) return "No databases available";
  return "Choose database";
};

export const getTableButtonText = (
  selectedTables,
  loadingTables,
  selectedDatabase,
  tables,
) => {
  if (selectedTables.length > 0) {
    return `${selectedTables.length} table${selectedTables.length > 1 ? "s" : ""} selected`;
  }
  if (loadingTables) return "Loading tables...";
  if (!selectedDatabase) return "Select database first";
  if (tables.length === 0) return "No tables available";
  return "Choose table(s)";
};

export const getRegionButtonText = (
  selectedRegion,
  loadingRegions,
  regions,
  regionDisplayMap,
) => {
  if (selectedRegion) return regionDisplayMap[selectedRegion] || selectedRegion;
  if (loadingRegions) return "Loading regions...";
  if (regions.length === 0) return "No regions available";
  return "Choose region";
};

export const getProtectionMessage = (hasFailures, previewMode) => {
  if (hasFailures) return "Some protections failed. Check console for details.";
  return previewMode
    ? "Preview generated successfully"
    : "Protection applied successfully";
};
